/**
 * ENG-5632 — static EC2 + EBS pricing for Mission Control cost estimates.
 *
 * Co-located with `ec2-capacity.ts` (the agent-capacity table) as the single
 * source of truth for "what does this host cost". Deliberately a static,
 * version-controlled snapshot rather than a live AWS Pricing API call:
 * deterministic, no extra runtime AWS integration, and trivially unit-tested.
 * Prices drift slowly — when they do, edit this file.
 *
 * Snapshot: AWS on-demand Linux pricing, captured 2026-05. Source:
 *   https://aws.amazon.com/ec2/pricing/on-demand/
 *   https://aws.amazon.com/ebs/pricing/
 * Figures are USD and intentionally rounded to the published rate.
 *
 * Pure functions, no node-only deps — safe for browser/edge bundles.
 */

export type Currency = 'USD';

/** Billable hours in a 730-hour "average" month (AWS's own convention). */
export const HOURS_PER_MONTH = 730;

/**
 * Root volume size (GiB) every Augmented-provisioned host launches with. The
 * single source of truth for the provisioned default: `ec2-provisioner.ts`
 * sets `VolumeSize` from this, and cost estimates that don't (yet) read a live
 * volume size assume it. ENG-5661 introduced the 100 GiB override (the AL2023
 * Minimal AMI's 2 GiB baseline launches dead-on-arrival). Co-located here with
 * the pricing so "what a host costs" has one home (browser-safe, no node deps).
 */
export const DEFAULT_HOST_ROOT_VOLUME_GIB = 100;

/**
 * gp3 includes a free baseline of 3,000 IOPS and 125 MB/s throughput per
 * volume; only provisioned capacity *above* these is billed.
 */
export const GP3_BASELINE_IOPS = 3000;
export const GP3_BASELINE_THROUGHPUT_MBPS = 125;

/**
 * On-demand $/hr per instance type, keyed by region. Mirrors the instance
 * families in `CAPACITY_TABLE` (ec2-capacity.ts). Add a region/type here when
 * we start running it — an uncatalogued type prices as "unknown" (null), not 0.
 */
const HOURLY_BY_REGION: Record<string, Record<string, number>> = {
  'ap-southeast-2': {
    't3.micro': 0.0132,
    't3.small': 0.0264,
    't3.medium': 0.0528,
    't3.large': 0.1056,
    't3.xlarge': 0.2112,
    't3.2xlarge': 0.4224,
    // m6i — general-purpose; in-fleet as of 2026-05 (ENG-5652).
    'm6i.large': 0.12,
    'm6i.xlarge': 0.24,
  },
  'us-east-1': {
    't3.micro': 0.0104,
    't3.small': 0.0208,
    't3.medium': 0.0416,
    't3.large': 0.0832,
    't3.xlarge': 0.1664,
    't3.2xlarge': 0.3328,
    // m6i — kept symmetric with ap-southeast-2 (ENG-5652).
    'm6i.large': 0.096,
    'm6i.xlarge': 0.192,
  },
};

/** EBS gp3 rates per region. */
interface Gp3Rates {
  /** $/GB-month of provisioned storage. */
  storagePerGiBMonth: number;
  /** $/provisioned-IOPS-month above the 3,000 baseline. */
  iopsPerMonth: number;
  /** $/provisioned-MBps-month above the 125 MB/s baseline. */
  throughputPerMbpsMonth: number;
}

// ap-southeast-2 (our fleet's home region) doubles as the EBS-rate fallback,
// so it's a named const — referenced both in the map and as the guaranteed
// non-undefined default when a priced region has no EBS row.
const FALLBACK_GP3_RATES: Gp3Rates = {
  storagePerGiBMonth: 0.096,
  iopsPerMonth: 0.006,
  throughputPerMbpsMonth: 0.048,
};

const EBS_GP3_BY_REGION: Record<string, Gp3Rates> = {
  'ap-southeast-2': FALLBACK_GP3_RATES,
  'us-east-1': { storagePerGiBMonth: 0.08, iopsPerMonth: 0.005, throughputPerMbpsMonth: 0.04 },
};

/**
 * Region used when the requested one isn't catalogued. Our fleet runs in
 * ap-southeast-2, so it's the least-surprising default — the caller is told
 * via `regionFallback: true` so the UI can flag the estimate as approximate.
 */
export const FALLBACK_PRICING_REGION = 'ap-southeast-2';

/** EBS volume facts (from a live DescribeVolumes call, or partial). */
export interface EbsVolumeSpec {
  /** Provisioned size in GiB. */
  sizeGiB: number;
  /** Provisioned IOPS (gp3). Omitted/null → treated as the free baseline. */
  iops?: number | null;
  /** Provisioned throughput in MB/s (gp3). Omitted/null → free baseline. */
  throughputMbps?: number | null;
  /** Volume type (e.g. 'gp3'). Only gp3 is priced today; others price storage-only at the gp3 rate as an approximation. */
  volumeType?: string | null;
}

export interface CostEstimateInput {
  instanceType: string | null | undefined;
  region: string | null | undefined;
  /** EBS volumes attached to the instance. Empty/omitted → no storage line. */
  volumes?: EbsVolumeSpec[];
}

export interface EbsCostBreakdown {
  storage: number;
  iops: number;
  throughput: number;
  total: number;
}

export interface CostEstimate {
  currency: Currency;
  hoursPerMonth: number;
  /** The region the estimate was priced against (may be the fallback). */
  pricedRegion: string;
  /** True when the requested region wasn't catalogued and the fallback was used. */
  regionFallback: boolean;
  instance: {
    type: string | null;
    /** null when the type isn't catalogued for the priced region. */
    hourly: number | null;
    monthly: number | null;
  };
  /** null when no volumes were supplied. */
  ebs: EbsCostBreakdown | null;
  /** Sum of the known line items. When `instancePriceKnown` is false this excludes the instance. */
  monthlyTotal: number;
  /** False when the instance type is unknown — the UI should mark the total as a lower bound. */
  instancePriceKnown: boolean;
}

/** Resolve the priced region: the requested one if catalogued, else the fallback. */
function resolvePricingRegion(region: string | null | undefined): { region: string; fallback: boolean } {
  if (region && HOURLY_BY_REGION[region]) return { region, fallback: false };
  return { region: FALLBACK_PRICING_REGION, fallback: true };
}

/** On-demand $/hr for an instance type in a region, or null when uncatalogued. */
export function hourlyInstanceCost(
  instanceType: string | null | undefined,
  region: string | null | undefined,
): number | null {
  if (!instanceType) return null;
  const { region: priced } = resolvePricingRegion(region);
  return HOURLY_BY_REGION[priced]?.[instanceType] ?? null;
}

/** Cost of a single EBS gp3 volume per month, line-itemized. */
function ebsVolumeCost(vol: EbsVolumeSpec, rates: Gp3Rates): EbsCostBreakdown {
  const storage = Math.max(0, vol.sizeGiB) * rates.storagePerGiBMonth;
  // The gp3 IOPS/throughput add-ons only apply to gp3 volumes. Other types
  // (gp2/io1/io2/st1/sc1) have different — and for io* provisioned — pricing
  // we don't model; charging them the gp3 add-on would overstate cost (e.g. an
  // io2 volume reports a high `Iops`). Per the EbsVolumeSpec contract, non-gp3
  // volumes are priced storage-only at the gp3 storage rate as an approximation.
  const isGp3 = (vol.volumeType ?? "").toLowerCase() === "gp3";
  const billableIops = isGp3
    ? Math.max(0, (vol.iops ?? GP3_BASELINE_IOPS) - GP3_BASELINE_IOPS)
    : 0;
  const billableThroughput = isGp3
    ? Math.max(0, (vol.throughputMbps ?? GP3_BASELINE_THROUGHPUT_MBPS) - GP3_BASELINE_THROUGHPUT_MBPS)
    : 0;
  const iops = billableIops * rates.iopsPerMonth;
  const throughput = billableThroughput * rates.throughputPerMbpsMonth;
  return { storage, iops, throughput, total: storage + iops + throughput };
}

/**
 * Estimate the monthly cost of a host: instance ($/hr × 730) + EBS storage +
 * provisioned IOPS/throughput above the gp3 baseline. Line-itemized so the UI
 * can render each component. Unknown instance type → instance line is null and
 * `instancePriceKnown` is false (total is then a lower bound covering EBS).
 */
export function estimateMonthlyCost(input: CostEstimateInput): CostEstimate {
  const { region: pricedRegion, fallback: regionFallback } = resolvePricingRegion(input.region);
  const rates: Gp3Rates = EBS_GP3_BY_REGION[pricedRegion] ?? FALLBACK_GP3_RATES;

  const hourly = hourlyInstanceCost(input.instanceType, input.region);
  const instanceMonthly = hourly === null ? null : hourly * HOURS_PER_MONTH;

  let ebs: EbsCostBreakdown | null = null;
  if (input.volumes && input.volumes.length > 0) {
    ebs = input.volumes.reduce<EbsCostBreakdown>(
      (acc, vol) => {
        const c = ebsVolumeCost(vol, rates);
        return {
          storage: acc.storage + c.storage,
          iops: acc.iops + c.iops,
          throughput: acc.throughput + c.throughput,
          total: acc.total + c.total,
        };
      },
      { storage: 0, iops: 0, throughput: 0, total: 0 },
    );
  }

  const monthlyTotal = (instanceMonthly ?? 0) + (ebs?.total ?? 0);

  return {
    currency: 'USD',
    hoursPerMonth: HOURS_PER_MONTH,
    pricedRegion,
    regionFallback,
    instance: {
      type: input.instanceType ?? null,
      hourly,
      monthly: instanceMonthly,
    },
    ebs,
    monthlyTotal,
    instancePriceKnown: hourly !== null,
  };
}

/** Catalogued pricing regions, for tests / UI hints. */
export const KNOWN_PRICING_REGIONS: ReadonlyArray<string> = Object.keys(HOURLY_BY_REGION);
